import { __awaiter, __extends, __generator } from "tslib";
import * as React from 'react';
import animeManager from 'shared/anime/animeManager';
import { buildAnimeTimelinesForChildren } from 'shared/anime/buildAnimeTimelines';
import { layoutMatcher } from 'shared/layoutMatcher';
import pluginLoader from 'shared/plugins/pluginLoader';
import SymbolMapper from 'shared/SymbolMapper';
import { setUTMOverrides } from 'shared/utils/ad';
import { debug } from 'shared/utils/debug';
import { dispatchGlobalEvent, getWindowSize } from 'shared/utils/dom';
import { values } from 'shared/utils/misc';
import preloadAdAssets from 'shared/utils/preloadAdAssets';
import { parseQueryString } from 'shared/utils/urls/misc';
import { symbolMap } from './Symbols';
var AdContainer = /** @class */ (function (_super) {
    __extends(AdContainer, _super);
    function AdContainer() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            layoutId: AdContainer.determineLayout(),
        };
        /**
         * As long as we aren't in static generation mode,
         */
        _this.preloadPlugins = function (ad, layoutId, timeParam, isProof) { return __awaiter(_this, void 0, void 0, function () {
            var plugins;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (timeParam !== undefined) {
                            return [2 /*return*/, {}];
                        }
                        return [4 /*yield*/, pluginLoader(ad, layoutId, isProof)];
                    case 1:
                        plugins = _a.sent();
                        return [4 /*yield*/, this.preloadAdaptScript(ad)];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.preloadProofCommunication()];
                    case 3:
                        _a.sent();
                        return [2 /*return*/, plugins];
                }
            });
        }); };
        _this.preloadAdaptScript = function (ad) { return __awaiter(_this, void 0, void 0, function () {
            var module_1, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (ad.script.trim().length === 0) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, import(
                            /* webpackChunkName: "script" */ 'shared/adaptScript')];
                    case 2:
                        module_1 = _a.sent();
                        this.adaptScript = module_1.default;
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        debug('failed to load AdaptScript', e_1);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        _this.preloadProofCommunication = function () { return __awaiter(_this, void 0, void 0, function () {
            var isProof, module_2, iframeAdToProof, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        isProof = !!parseQueryString(window.location.href, true).proof;
                        if (!isProof) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, import(
                            /* webpackChunkName: "proof" */ 'shared/utils/iframe/iframeAdToProof')];
                    case 2:
                        module_2 = _a.sent();
                        iframeAdToProof = module_2.default;
                        iframeAdToProof.listen();
                        return [3 /*break*/, 4];
                    case 3:
                        e_2 = _a.sent();
                        debug('failed to load Adapt Proof', e_2);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        /**
         * If the appropriate Layout changes,
         * rebuild the DOM and timeline.
         */
        _this.handleResize = function () {
            var layoutId = AdContainer.determineLayout();
            if (_this.state.layoutId === layoutId) {
                return;
            }
            _this.setState({ layoutId: layoutId }, function () { return _this.buildTimelinesAndPlay(layoutId); });
        };
        return _this;
    }
    AdContainer.prototype.componentDidMount = function () {
        var stageElm = document.querySelector('.stage');
        stageElm.style.opacity = '0';
        window.addEventListener('resize', this.handleResize);
        this.buildTimelinesAndPlay(this.state.layoutId);
    };
    AdContainer.prototype.componentWillUnmount = function () {
        window.removeEventListener('resize', this.handleResize);
        animeManager.pause();
        if (this.adaptScript) {
            this.adaptScript.reset();
        }
    };
    AdContainer.prototype.render = function () {
        var ad = AdContainer.getAd();
        var layoutId = this.state.layoutId;
        return (React.createElement(SymbolMapper, { ad: ad, layoutId: layoutId, symbolId: "stage", symbolMap: symbolMap }));
    };
    /**
     * Build AnimeJS timelines and begin playing the ad.
     */
    AdContainer.prototype.buildTimelinesAndPlay = function (layoutId) {
        return __awaiter(this, void 0, void 0, function () {
            var ad, timelines, queryParams, timeParam, isProof, stageElm, plugins, timeout;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        ad = AdContainer.getAd();
                        timelines = buildAnimeTimelinesForChildren(ad, layoutId, 'stage');
                        queryParams = parseQueryString(window.location.href, true);
                        timeParam = queryParams.t;
                        isProof = !!queryParams.proof;
                        stageElm = document.querySelector('.stage');
                        stageElm.style.opacity = '0';
                        if (this.adaptScript) {
                            this.adaptScript.reset();
                        }
                        // Attach UTM overrides to global namespace.
                        setUTMOverrides(ad, layoutId);
                        return [4 /*yield*/, preloadAdAssets(ad, layoutId)];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.preloadPlugins(ad, layoutId, timeParam, isProof)];
                    case 2:
                        plugins = _a.sent();
                        timeout = timeParam !== undefined && !isProof ? 0 : 125;
                        setTimeout(function () {
                            dispatchGlobalEvent('renderRequested');
                            // In Delivery only, animeManager must be defined here
                            // to avoid element froze and be dislocated before assets loaded
                            animeManager.update(timelines, {
                                autoplay: false,
                                loop: ad.loopCount,
                            });
                            if (_this.adaptScript) {
                                _this.adaptScript.processData(ad, layoutId, plugins);
                            }
                            // only show stage when everything is ready
                            stageElm.style.opacity = '1';
                            if (timeParam !== undefined) {
                                animeManager.seek(parseInt(timeParam, 10)).render();
                            }
                            else {
                                ad.autoPlay
                                    ? animeManager.play().render()
                                    : animeManager.render();
                            }
                            // Only observe scroll visible in non-proof environment
                            if (!isProof) {
                                var autoPlay = timeParam === undefined && ad.autoPlay;
                                animeManager.registerVisibleObserver(autoPlay);
                            }
                            dispatchGlobalEvent('timelineDuration', {
                                duration: animeManager.timeline.duration,
                            });
                        }, timeout);
                        return [2 /*return*/];
                }
            });
        });
    };
    AdContainer.getAd = function () { return window.ADAPT.ad; };
    /**
     * Determine the appropriate Layout
     * based on the current window size.
     */
    AdContainer.determineLayout = function () {
        var layoutParam = parseQueryString(window.location.href, true).layout;
        if (layoutParam) {
            return layoutParam;
        }
        var ad = AdContainer.getAd();
        var layouts = values(ad.layouts);
        var adSize = getWindowSize();
        var layoutId = layoutMatcher(adSize.width, adSize.height, layouts).id;
        return layoutId;
    };
    return AdContainer;
}(React.Component));
export default AdContainer;
